var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ActivityIndicator, ScrollView } from 'react-native';
import styled from 'styled-components/native';
import { SccPrimaryButton } from '@/components/atoms';
import { ScreenLayout } from '@/components/ScreenLayout';
import { color } from '@/constant/color';
import { font } from '@/constant/font';
import TextAreaV2 from '@/screens/PlaceFormV2Screen/components/TextAreaV2';
import { ElevatorCorrectionTargetDto, FloorMovingMethodTypeDto, InaccurateInfoCategoryDto, PlaceDoorDirectionTypeDto, ReportTargetTypeDto, StairInfo, StairHeightLevel, } from '@/generated-sources/openapi';
import useAppComponents from '@/hooks/useAppComponents';
import { UploadProgressOverlay } from '@/components/UploadProgressOverlay';
import { showUploadFailedToast, useImageUploadWithProgress, } from '@/hooks/useImageUploadWithProgress';
import usePost from '@/hooks/usePost';
import ToastUtils from '@/utils/ToastUtils';
import { getAccessibilitySections } from '../PlaceDetailV2Screen/components/PlaceInfo.utils';
import BuildingEntranceCorrectionSection from './sections/BuildingEntranceCorrectionSection';
import PlaceEntranceCorrectionSection from './sections/PlaceEntranceCorrectionSection';
import FloorCorrectionSection from './sections/FloorCorrectionSection';
import DoorTypeCorrectionSection from './sections/DoorTypeCorrectionSection';
import ElevatorCorrectionSection from './sections/ElevatorCorrectionSection';
import AccessLevelCorrectionSection from './sections/AccessLevelCorrectionSection';
import PhotoCorrectionSection from './sections/PhotoCorrectionSection';
/** 카테고리별 전용 DTO 빌드 함수 */
function buildEntranceCorrection(placeCorrection, isStandaloneBuilding, finalEntranceUrls) {
    return {
        stairInfo: placeCorrection.stairInfo,
        stairHeightLevel: placeCorrection.stairHeightLevel,
        hasSlope: placeCorrection.hasSlope,
        doorDirectionType: placeCorrection.doorDirectionType,
        isStandaloneBuilding: isStandaloneBuilding,
        entranceImageUrls: finalEntranceUrls.length > 0 ? finalEntranceUrls : undefined,
    };
}
function buildBuildingEntranceCorrection(buildingCorrection, finalBaEntranceUrls) {
    return {
        entranceStairInfo: buildingCorrection.entranceStairInfo,
        entranceStairHeightLevel: buildingCorrection.entranceStairHeightLevel,
        hasSlope: buildingCorrection.hasSlope,
        entranceDoorTypes: buildingCorrection.entranceDoorTypes,
        entranceImageUrls: finalBaEntranceUrls.length > 0 ? finalBaEntranceUrls : undefined,
    };
}
function buildFloorCorrection(placeCorrection, isStandaloneBuilding, finalElevatorUrls) {
    var _a;
    // elevatorAccessibility는 PLACE_ELEVATOR가 선택된 경우에만 전송한다.
    // BUILDING_ELEVATOR만 선택된 경우 PA elevator 정보를 보내면
    // 서버에서 BA elevator 업데이트로 잘못 사용될 수 있다.
    const hasPlaceElevator = (_a = placeCorrection.floorMovingMethodTypes) === null || _a === void 0 ? void 0 : _a.includes(FloorMovingMethodTypeDto.PlaceElevator);
    const ea = hasPlaceElevator
        ? placeCorrection.elevatorAccessibility
        : undefined;
    return {
        floors: placeCorrection.floors,
        isStandaloneBuilding: isStandaloneBuilding,
        floorMovingMethodTypes: placeCorrection.floorMovingMethodTypes,
        elevatorAccessibility: ea
            ? {
                stairInfo: ea.stairInfo,
                stairHeightLevel: ea.stairHeightLevel,
                hasSlope: ea.hasSlope,
                imageUrls: finalElevatorUrls.length > 0 ? finalElevatorUrls : undefined,
            }
            : undefined,
    };
}
function buildElevatorCorrection(placeCorrection, buildingCorrection, finalElevatorUrls, target) {
    if (target === ElevatorCorrectionTargetDto.Ba) {
        const ea = buildingCorrection.elevatorAccessibility;
        return {
            target: ElevatorCorrectionTargetDto.Ba,
            hasElevator: buildingCorrection.hasElevator,
            elevatorAccessibility: ea
                ? {
                    stairInfo: ea.stairInfo,
                    stairHeightLevel: ea.stairHeightLevel,
                    hasSlope: ea.hasSlope,
                }
                : undefined,
            elevatorImageUrls: finalElevatorUrls.length > 0 ? finalElevatorUrls : undefined,
        };
    }
    // PA case (existing logic)
    const ea = placeCorrection.elevatorAccessibility;
    return {
        target: ElevatorCorrectionTargetDto.Pa,
        elevatorAccessibility: ea
            ? {
                stairInfo: ea.stairInfo,
                stairHeightLevel: ea.stairHeightLevel,
                hasSlope: ea.hasSlope,
            }
            : undefined,
        elevatorImageUrls: finalElevatorUrls.length > 0 ? finalElevatorUrls : undefined,
    };
}
function buildDoorTypeCorrection(placeCorrection) {
    return {
        entranceDoorTypes: placeCorrection.entranceDoorTypes,
    };
}
function buildAccessLevelCorrection(stairInfo, stairHeightLevel) {
    return {
        stairInfo,
        stairHeightLevel,
    };
}
function buildPhotoCorrection(finalEntranceUrls, finalElevatorUrls, needsBaPhotos, finalBaEntranceUrls, finalBaElevatorUrls) {
    return {
        entranceImageUrls: finalEntranceUrls.length > 0 ? finalEntranceUrls : undefined,
        elevatorImageUrls: finalElevatorUrls.length > 0 ? finalElevatorUrls : undefined,
        baEntranceImageUrls: needsBaPhotos && finalBaEntranceUrls.length > 0
            ? finalBaEntranceUrls
            : undefined,
        baElevatorImageUrls: needsBaPhotos && finalBaElevatorUrls.length > 0
            ? finalBaElevatorUrls
            : undefined,
    };
}
/** Access level (0-5) → stairInfo + stairHeightLevel 역매핑 */
function accessLevelToStairFields(level) {
    switch (level) {
        case 0:
            return { stairInfo: StairInfo.None };
        case 1:
            return {
                stairInfo: StairInfo.One,
                stairHeightLevel: StairHeightLevel.HalfThumb,
            };
        case 2:
            return {
                stairInfo: StairInfo.One,
                stairHeightLevel: StairHeightLevel.Thumb,
            };
        case 3:
            return {
                stairInfo: StairInfo.One,
                stairHeightLevel: StairHeightLevel.OverThumb,
            };
        case 4:
            return { stairInfo: StairInfo.TwoToFive };
        case 5:
            return { stairInfo: StairInfo.OverSix };
        default:
            return { stairInfo: StairInfo.Undefined };
    }
}
/** stairInfo + stairHeightLevel → access level (0-5) 순매핑 */
function stairFieldsToAccessLevel(stairInfo, stairHeightLevel) {
    if (!stairInfo || stairInfo === StairInfo.Undefined) {
        return undefined;
    }
    switch (stairInfo) {
        case StairInfo.None:
            return 0;
        case StairInfo.One:
            switch (stairHeightLevel) {
                case StairHeightLevel.HalfThumb:
                    return 1;
                case StairHeightLevel.Thumb:
                    return 2;
                case StairHeightLevel.OverThumb:
                    return 3;
                default:
                    return 1;
            }
        case StairInfo.TwoToFive:
            return 4;
        case StairInfo.OverSix:
            return 5;
        default:
            return undefined;
    }
}
export default function ReportCorrectionFormScreen({ route, navigation, }) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l;
    const { placeId, inaccurateCategory, elevatorTarget, onSubmitSuccess } = route.params;
    const category = inaccurateCategory;
    const { api } = useAppComponents();
    const queryClient = useQueryClient();
    const { uploadImages, uploadProgress } = useImageUploadWithProgress();
    const [isLoading, setIsLoading] = useState(true);
    const [accessibilityData, setAccessibilityData] = useState(null);
    const [placeCorrection, setPlaceCorrection] = useState({});
    const [buildingCorrection, setBuildingCorrection] = useState({});
    const [noteText, setNoteText] = useState('');
    // Photo state
    const [newEntrancePhotos, setNewEntrancePhotos] = useState([]);
    const [newElevatorPhotos, setNewElevatorPhotos] = useState([]);
    const [deletedEntrancePhotoIndices, setDeletedEntrancePhotoIndices] = useState([]);
    const [deletedElevatorPhotoIndices, setDeletedElevatorPhotoIndices] = useState([]);
    // Replaced photos
    const [replacedEntrancePhotos, setReplacedEntrancePhotos] = useState(new Map());
    const [replacedElevatorPhotos, setReplacedElevatorPhotos] = useState(new Map());
    // BA (Building Accessibility) photo state
    const [newBaEntrancePhotos, setNewBaEntrancePhotos] = useState([]);
    const [newBaElevatorPhotos, setNewBaElevatorPhotos] = useState([]);
    const [deletedBaEntrancePhotoIndices, setDeletedBaEntrancePhotoIndices] = useState([]);
    const [deletedBaElevatorPhotoIndices, setDeletedBaElevatorPhotoIndices] = useState([]);
    const [replacedBaEntrancePhotos, setReplacedBaEntrancePhotos] = useState(new Map());
    const [replacedBaElevatorPhotos, setReplacedBaElevatorPhotos] = useState(new Map());
    // Access level state
    const [selectedAccessLevel, setSelectedAccessLevel] = useState(undefined);
    // Floor form state (for submit validation)
    const [floorFormState, setFloorFormState] = useState(null);
    // Refs for initial state (to detect changes)
    const initialPlaceCorrectionRef = useRef({});
    const initialBuildingCorrectionRef = useRef({});
    useEffect(() => {
        // PDP에서 이미 fetch한 접근성 데이터를 캐시에서 읽어 prefill
        const cached = queryClient.getQueryData([
            'PlaceDetailV2',
            placeId,
            'Accessibility',
        ]);
        if (!cached) {
            ToastUtils.show('접근성 정보를 불러올 수 없습니다.');
            navigation.goBack();
            return;
        }
        setAccessibilityData(cached);
        const pa = cached.placeAccessibility;
        const ba = cached.buildingAccessibility;
        if (pa) {
            const paCorrection = {
                stairInfo: pa.stairInfo,
                stairHeightLevel: pa.stairHeightLevel,
                hasSlope: pa.hasSlope,
                floors: pa.floors,
                entranceDoorTypes: pa.entranceDoorTypes,
                floorMovingMethodTypes: pa.floorMovingMethodTypes,
                elevatorAccessibility: pa.floorMovingElevatorAccessibility,
                doorDirectionType: pa.doorDirectionType,
            };
            setPlaceCorrection(paCorrection);
            initialPlaceCorrectionRef.current = paCorrection;
        }
        if (ba) {
            const baCorrection = {
                entranceStairInfo: ba.entranceStairInfo,
                entranceStairHeightLevel: ba.entranceStairHeightLevel,
                hasSlope: ba.hasSlope,
                hasElevator: ba.hasElevator,
                entranceDoorTypes: ba.entranceDoorTypes,
                elevatorAccessibility: ba.hasElevator
                    ? {
                        stairInfo: ba.elevatorStairInfo,
                        stairHeightLevel: ba.elevatorStairHeightLevel,
                        hasSlope: ba.elevatorHasSlope,
                    }
                    : undefined,
            };
            setBuildingCorrection(baCorrection);
            initialBuildingCorrectionRef.current = baCorrection;
        }
        setIsLoading(false);
    }, [placeId, queryClient, navigation]);
    // BA photos are needed when place entrance is inside building (not standalone)
    const needsBaPhotos = useMemo(() => {
        var _a, _b;
        return !((_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.isStandaloneBuilding) &&
            ((_b = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _b === void 0 ? void 0 : _b.doorDirectionType) ===
                PlaceDoorDirectionTypeDto.InsideBuilding;
    }, [accessibilityData]);
    // Photo section visibility flags (match PDP home tab)
    const showPlaceElevatorPhotos = useMemo(() => {
        var _a, _b, _c;
        return (_c = (_b = (_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.floorMovingMethodTypes) === null || _b === void 0 ? void 0 : _b.includes(FloorMovingMethodTypeDto.PlaceElevator)) !== null && _c !== void 0 ? _c : false;
    }, [accessibilityData]);
    const pdpSections = useMemo(() => {
        var _a;
        const pa = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility;
        if (!pa) {
            return [];
        }
        const floors = (_a = pa.floors) !== null && _a !== void 0 ? _a : [];
        const isMultiFloor = floors.length > 1 || (floors.length === 1 && floors[0] !== 1);
        return getAccessibilitySections({
            isStandalone: pa.isStandaloneBuilding === true,
            doorDir: pa.doorDirectionType,
            isMultiFloor,
            hasV2Fields: pa.entranceDoorTypes != null,
            hasBuildingAccessibility: (accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.buildingAccessibility) != null,
        });
    }, [accessibilityData]);
    const showBaEntrancePhotos = useMemo(() => pdpSections.includes('건물 출입구'), [pdpSections]);
    const showBaElevatorPhotos = useMemo(() => {
        var _a, _b;
        return pdpSections.includes('건물 출입구') &&
            ((_b = (_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.buildingAccessibility) === null || _a === void 0 ? void 0 : _a.hasElevator) !== null && _b !== void 0 ? _b : false);
    }, [pdpSections, accessibilityData]);
    const hasChanges = useMemo(() => {
        var _a, _b;
        return (JSON.stringify(placeCorrection) !==
            JSON.stringify(initialPlaceCorrectionRef.current) ||
            JSON.stringify(buildingCorrection) !==
                JSON.stringify(initialBuildingCorrectionRef.current) ||
            noteText !== '' ||
            newEntrancePhotos.length > 0 ||
            newElevatorPhotos.length > 0 ||
            deletedEntrancePhotoIndices.length > 0 ||
            deletedElevatorPhotoIndices.length > 0 ||
            replacedEntrancePhotos.size > 0 ||
            replacedElevatorPhotos.size > 0 ||
            newBaEntrancePhotos.length > 0 ||
            newBaElevatorPhotos.length > 0 ||
            deletedBaEntrancePhotoIndices.length > 0 ||
            deletedBaElevatorPhotoIndices.length > 0 ||
            replacedBaEntrancePhotos.size > 0 ||
            replacedBaElevatorPhotos.size > 0 ||
            (selectedAccessLevel !== undefined &&
                selectedAccessLevel !==
                    stairFieldsToAccessLevel((_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.stairInfo, (_b = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _b === void 0 ? void 0 : _b.stairHeightLevel)));
    }, [
        placeCorrection,
        buildingCorrection,
        noteText,
        newEntrancePhotos,
        newElevatorPhotos,
        deletedEntrancePhotoIndices,
        deletedElevatorPhotoIndices,
        replacedEntrancePhotos,
        replacedElevatorPhotos,
        newBaEntrancePhotos,
        newBaElevatorPhotos,
        deletedBaEntrancePhotoIndices,
        deletedBaElevatorPhotoIndices,
        replacedBaEntrancePhotos,
        replacedBaElevatorPhotos,
        selectedAccessLevel,
        accessibilityData,
    ]);
    // 기존에 사진이 있었는데 모두 삭제되면 제출 불가
    // 단, 엘리베이터를 "없음"으로 교정한 경우 사진이 0장이어도 violation 아님
    const hasPhotoViolation = useMemo(() => {
        var _a, _b, _c, _d, _e;
        const existingEntranceUrls = (_b = (_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.imageUrls) !== null && _b !== void 0 ? _b : [];
        const existingElevatorUrls = (_e = (_d = (_c = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _c === void 0 ? void 0 : _c.floorMovingElevatorAccessibility) === null || _d === void 0 ? void 0 : _d.imageUrls) !== null && _e !== void 0 ? _e : [];
        // 최종 입구 사진 수: 삭제 안 된 기존 사진 + 새 사진
        const finalEntranceCount = existingEntranceUrls.filter((_, idx) => !deletedEntrancePhotoIndices.includes(idx)).length + newEntrancePhotos.length;
        // 최종 엘리베이터 사진 수: 삭제 안 된 기존 사진 + 새 사진
        const finalElevatorCount = existingElevatorUrls.filter((_, idx) => !deletedElevatorPhotoIndices.includes(idx)).length + newElevatorPhotos.length;
        const entranceViolation = existingEntranceUrls.length > 0 && finalEntranceCount === 0;
        // 엘리베이터를 "없음"으로 교정하면 (elevatorAccessibility === undefined)
        // 사진이 불필요하므로 violation이 아님
        const correctedHasElevator = placeCorrection.elevatorAccessibility !== undefined;
        const elevatorViolation = correctedHasElevator &&
            existingElevatorUrls.length > 0 &&
            finalElevatorCount === 0;
        return entranceViolation || elevatorViolation;
    }, [
        accessibilityData,
        deletedEntrancePhotoIndices,
        deletedElevatorPhotoIndices,
        newEntrancePhotos,
        newElevatorPhotos,
        placeCorrection.elevatorAccessibility,
    ]);
    const submitMutation = usePost(['ReportCorrectionForm', 'Submit'], (_a) => __awaiter(this, [_a], void 0, function* ({ finalEntranceUrls, finalElevatorUrls, finalBaEntranceUrls, finalBaElevatorUrls, }) {
        var _b, _c, _d, _e, _f;
        // Build category-specific correction DTO
        const isStandaloneBuilding = (_b = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _b === void 0 ? void 0 : _b.isStandaloneBuilding;
        let entrance;
        let buildingEntrance;
        let floor;
        let elevator;
        let doorType;
        let accessLevel;
        let photo;
        switch (category) {
            case InaccurateInfoCategoryDto.PlaceEntrance:
                entrance = buildEntranceCorrection(placeCorrection, isStandaloneBuilding, finalEntranceUrls);
                break;
            case InaccurateInfoCategoryDto.BuildingEntrance:
                buildingEntrance = buildBuildingEntranceCorrection(buildingCorrection, finalBaEntranceUrls);
                break;
            case InaccurateInfoCategoryDto.Floor:
                floor = buildFloorCorrection(placeCorrection, isStandaloneBuilding, finalElevatorUrls);
                break;
            case InaccurateInfoCategoryDto.Elevator:
                elevator = buildElevatorCorrection(placeCorrection, buildingCorrection, elevatorTarget === ElevatorCorrectionTargetDto.Ba
                    ? finalBaElevatorUrls
                    : finalElevatorUrls, elevatorTarget !== null && elevatorTarget !== void 0 ? elevatorTarget : ElevatorCorrectionTargetDto.Pa);
                break;
            case InaccurateInfoCategoryDto.DoorType:
                doorType = buildDoorTypeCorrection(placeCorrection);
                break;
            case InaccurateInfoCategoryDto.AccessLevel: {
                let stairFields;
                if (selectedAccessLevel !== undefined) {
                    stairFields = accessLevelToStairFields(selectedAccessLevel);
                }
                else {
                    stairFields = {
                        stairInfo: (_c = placeCorrection.stairInfo) !== null && _c !== void 0 ? _c : StairInfo.Undefined,
                        stairHeightLevel: placeCorrection.stairHeightLevel,
                    };
                }
                accessLevel = buildAccessLevelCorrection(stairFields.stairInfo, stairFields.stairHeightLevel);
                break;
            }
            case InaccurateInfoCategoryDto.Photo:
                photo = buildPhotoCorrection(finalEntranceUrls, finalElevatorUrls, needsBaPhotos, finalBaEntranceUrls, finalBaElevatorUrls);
                break;
            case InaccurateInfoCategoryDto.Other:
                // Other: noteText만 전달
                break;
            default: {
                const _exhaustiveCheck = category;
                throw new Error(`Unknown category: ${_exhaustiveCheck}`);
            }
        }
        const result = yield api.reportAccessibilityPost({
            placeId,
            placeAccessibilityId: (_d = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _d === void 0 ? void 0 : _d.id,
            buildingAccessibilityId: (_e = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.buildingAccessibility) === null || _e === void 0 ? void 0 : _e.id,
            targetType: ReportTargetTypeDto.PlaceAccessibility,
            reason: 'INACCURATE_INFO',
            detail: noteText || undefined,
            correction: {
                type: category,
                placeEntrance: entrance,
                buildingEntrance,
                floor,
                elevator,
                doorType,
                accessLevel,
                photo,
                noteText: noteText || undefined,
            },
        });
        // PDP 접근성 데이터를 refetch하도록 캐시 무효화
        yield queryClient.invalidateQueries({
            queryKey: ['PlaceDetailV2', placeId],
        });
        const isAutoResolved = ((_f = result.data) === null || _f === void 0 ? void 0 : _f.isAutoResolved) === true;
        ToastUtils.show(isAutoResolved ? '신고가 바로 반영되었어요!' : '신고가 접수되었어요.');
        onSubmitSuccess === null || onSubmitSuccess === void 0 ? void 0 : onSubmitSuccess();
        navigation.goBack();
    }));
    const [isUploading, setIsUploading] = useState(false);
    const handleSubmitPress = () => __awaiter(this, void 0, void 0, function* () {
        setIsUploading(true);
        try {
            // 1. Upload new PA photos
            const uploadedEntranceUrls = newEntrancePhotos.length > 0
                ? yield uploadImages(api, newEntrancePhotos, undefined, '입구 사진')
                : [];
            const uploadedElevatorUrls = newElevatorPhotos.length > 0
                ? yield uploadImages(api, newElevatorPhotos, undefined, '엘리베이터 사진')
                : [];
            // Upload replaced PA entrance photos
            const replacedEntranceEntries = Array.from(replacedEntrancePhotos.entries());
            const uploadedReplacedEntranceUrls = replacedEntranceEntries.length > 0
                ? yield uploadImages(api, replacedEntranceEntries.map(([_, photo]) => photo), undefined, '입구 사진')
                : [];
            const replacedEntranceUrlMap = new Map();
            replacedEntranceEntries.forEach(([idx], i) => {
                replacedEntranceUrlMap.set(idx, uploadedReplacedEntranceUrls[i]);
            });
            // Upload replaced PA elevator photos
            const replacedElevatorEntries = Array.from(replacedElevatorPhotos.entries());
            const uploadedReplacedElevatorUrls = replacedElevatorEntries.length > 0
                ? yield uploadImages(api, replacedElevatorEntries.map(([_, photo]) => photo), undefined, '엘리베이터 사진')
                : [];
            const replacedElevatorUrlMap = new Map();
            replacedElevatorEntries.forEach(([idx], i) => {
                replacedElevatorUrlMap.set(idx, uploadedReplacedElevatorUrls[i]);
            });
            // 1b. Upload BA photos (only if needsBaPhotos)
            const uploadedBaEntranceUrls = needsBaPhotos && newBaEntrancePhotos.length > 0
                ? yield uploadImages(api, newBaEntrancePhotos, undefined, '건물 입구 사진')
                : [];
            const uploadedBaElevatorUrls = needsBaPhotos && newBaElevatorPhotos.length > 0
                ? yield uploadImages(api, newBaElevatorPhotos, undefined, '건물 엘리베이터 사진')
                : [];
            // Upload replaced BA entrance photos
            const replacedBaEntranceEntries = Array.from(replacedBaEntrancePhotos.entries());
            const uploadedReplacedBaEntranceUrls = needsBaPhotos && replacedBaEntranceEntries.length > 0
                ? yield uploadImages(api, replacedBaEntranceEntries.map(([_, photo]) => photo), undefined, '건물 입구 사진')
                : [];
            const replacedBaEntranceUrlMap = new Map();
            replacedBaEntranceEntries.forEach(([idx], i) => {
                replacedBaEntranceUrlMap.set(idx, uploadedReplacedBaEntranceUrls[i]);
            });
            // Upload replaced BA elevator photos
            const replacedBaElevatorEntries = Array.from(replacedBaElevatorPhotos.entries());
            const uploadedReplacedBaElevatorUrls = needsBaPhotos && replacedBaElevatorEntries.length > 0
                ? yield uploadImages(api, replacedBaElevatorEntries.map(([_, photo]) => photo), undefined, '건물 엘리베이터 사진')
                : [];
            const replacedBaElevatorUrlMap = new Map();
            replacedBaElevatorEntries.forEach(([idx], i) => {
                replacedBaElevatorUrlMap.set(idx, uploadedReplacedBaElevatorUrls[i]);
            });
            // 2. Build final PA photo URL lists (existing with replacements - deleted + new)
            const finalEntranceUrls = entranceImageUrls
                .map((url, idx) => {
                var _a;
                if (deletedEntrancePhotoIndices.includes(idx)) {
                    return null;
                }
                return (_a = replacedEntranceUrlMap.get(idx)) !== null && _a !== void 0 ? _a : url;
            })
                .filter((url) => url !== null)
                .concat(uploadedEntranceUrls);
            const finalElevatorUrls = elevatorImageUrls
                .map((url, idx) => {
                var _a;
                if (deletedElevatorPhotoIndices.includes(idx)) {
                    return null;
                }
                return (_a = replacedElevatorUrlMap.get(idx)) !== null && _a !== void 0 ? _a : url;
            })
                .filter((url) => url !== null)
                .concat(uploadedElevatorUrls);
            // 2b. Build final BA photo URL lists
            const finalBaEntranceUrls = needsBaPhotos
                ? baEntranceImageUrls
                    .map((url, idx) => {
                    var _a;
                    if (deletedBaEntrancePhotoIndices.includes(idx)) {
                        return null;
                    }
                    return (_a = replacedBaEntranceUrlMap.get(idx)) !== null && _a !== void 0 ? _a : url;
                })
                    .filter((url) => url !== null)
                    .concat(uploadedBaEntranceUrls)
                : [];
            const finalBaElevatorUrls = needsBaPhotos
                ? baElevatorImageUrls
                    .map((url, idx) => {
                    var _a;
                    if (deletedBaElevatorPhotoIndices.includes(idx)) {
                        return null;
                    }
                    return (_a = replacedBaElevatorUrlMap.get(idx)) !== null && _a !== void 0 ? _a : url;
                })
                    .filter((url) => url !== null)
                    .concat(uploadedBaElevatorUrls)
                : [];
            submitMutation.mutate({
                finalEntranceUrls,
                finalElevatorUrls,
                finalBaEntranceUrls,
                finalBaElevatorUrls,
            });
        }
        catch (e) {
            showUploadFailedToast(e);
        }
        finally {
            setIsUploading(false);
        }
    });
    const isSubmitDisabled = useMemo(() => {
        var _a, _b;
        if (!hasChanges ||
            submitMutation.isPending ||
            isUploading ||
            hasPhotoViolation) {
            return true;
        }
        // FLOOR 카테고리: 층간이동 필수인 경우 체크
        if (category === InaccurateInfoCategoryDto.Floor && floorFormState) {
            if (floorFormState.conditions.showFloorMovement &&
                !((_a = placeCorrection.floorMovingMethodTypes) === null || _a === void 0 ? void 0 : _a.length)) {
                return true;
            }
            // PLACE_ELEVATOR가 선택되면 엘리베이터 정보 필수
            if ((_b = placeCorrection.floorMovingMethodTypes) === null || _b === void 0 ? void 0 : _b.includes(FloorMovingMethodTypeDto.PlaceElevator)) {
                const ea = placeCorrection.elevatorAccessibility;
                if (!ea) {
                    return true;
                }
                if (!ea.stairInfo || ea.stairInfo === StairInfo.Undefined) {
                    return true;
                }
                if (ea.hasSlope === undefined || ea.hasSlope === null) {
                    return true;
                }
            }
        }
        // BUILDING_ENTRANCE 카테고리: 필수 필드 검증
        if (category === InaccurateInfoCategoryDto.BuildingEntrance) {
            if (!buildingCorrection.entranceStairInfo ||
                buildingCorrection.entranceStairInfo === StairInfo.Undefined) {
                return true;
            }
            if (buildingCorrection.hasSlope === undefined ||
                buildingCorrection.hasSlope === null) {
                return true;
            }
        }
        // ELEVATOR 카테고리: 엘리베이터 있음인데 세부 정보 미선택
        if (category === InaccurateInfoCategoryDto.Elevator) {
            const ea = elevatorTarget === ElevatorCorrectionTargetDto.Ba
                ? buildingCorrection.elevatorAccessibility
                : placeCorrection.elevatorAccessibility;
            if (ea !== undefined) {
                if (!ea.stairInfo || ea.stairInfo === StairInfo.Undefined) {
                    return true;
                }
                if (ea.hasSlope === undefined || ea.hasSlope === null) {
                    return true;
                }
            }
        }
        return false;
    }, [
        hasChanges,
        submitMutation.isPending,
        isUploading,
        hasPhotoViolation,
        category,
        elevatorTarget,
        floorFormState,
        placeCorrection.floorMovingMethodTypes,
        placeCorrection.elevatorAccessibility,
        buildingCorrection.entranceStairInfo,
        buildingCorrection.hasSlope,
        buildingCorrection.elevatorAccessibility,
    ]);
    const updatePlaceField = useCallback((key, value) => {
        setPlaceCorrection(prev => (Object.assign(Object.assign({}, prev), { [key]: value })));
    }, []);
    const handleDeleteExistingEntrancePhoto = useCallback((index) => {
        setDeletedEntrancePhotoIndices(prev => [...prev, index]);
        // Also remove from replaced if it was replaced
        setReplacedEntrancePhotos(prev => {
            const next = new Map(prev);
            next.delete(index);
            return next;
        });
    }, []);
    const handleDeleteExistingElevatorPhoto = useCallback((index) => {
        setDeletedElevatorPhotoIndices(prev => [...prev, index]);
        setReplacedElevatorPhotos(prev => {
            const next = new Map(prev);
            next.delete(index);
            return next;
        });
    }, []);
    const handleReplaceExistingEntrancePhoto = useCallback((index, photo) => {
        setReplacedEntrancePhotos(prev => new Map(prev).set(index, photo));
    }, []);
    const handleReplaceExistingElevatorPhoto = useCallback((index, photo) => {
        setReplacedElevatorPhotos(prev => new Map(prev).set(index, photo));
    }, []);
    // BA photo handlers
    const handleDeleteExistingBaEntrancePhoto = useCallback((index) => {
        setDeletedBaEntrancePhotoIndices(prev => [...prev, index]);
        setReplacedBaEntrancePhotos(prev => {
            const next = new Map(prev);
            next.delete(index);
            return next;
        });
    }, []);
    const handleDeleteExistingBaElevatorPhoto = useCallback((index) => {
        setDeletedBaElevatorPhotoIndices(prev => [...prev, index]);
        setReplacedBaElevatorPhotos(prev => {
            const next = new Map(prev);
            next.delete(index);
            return next;
        });
    }, []);
    const handleReplaceExistingBaEntrancePhoto = useCallback((index, photo) => {
        setReplacedBaEntrancePhotos(prev => new Map(prev).set(index, photo));
    }, []);
    const handleReplaceExistingBaElevatorPhoto = useCallback((index, photo) => {
        setReplacedBaElevatorPhotos(prev => new Map(prev).set(index, photo));
    }, []);
    if (isLoading) {
        return (_jsx(ScreenLayout, { isHeaderVisible: true, isKeyboardAvoidingView: true, children: _jsx(LoadingContainer, { children: _jsx(ActivityIndicator, { size: "large", color: color.brandColor }) }) }));
    }
    const entranceImageUrls = (_b = (_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.imageUrls) !== null && _b !== void 0 ? _b : [];
    const elevatorImageUrls = (_e = (_d = (_c = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _c === void 0 ? void 0 : _c.floorMovingElevatorAccessibility) === null || _d === void 0 ? void 0 : _d.imageUrls) !== null && _e !== void 0 ? _e : [];
    // BA image URLs (from BuildingAccessibility)
    const baEntranceImageUrls = (_h = (_g = (_f = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.buildingAccessibility) === null || _f === void 0 ? void 0 : _f.entranceImages) === null || _g === void 0 ? void 0 : _g.map(img => img.imageUrl)) !== null && _h !== void 0 ? _h : [];
    const baElevatorImageUrls = (_l = (_k = (_j = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.buildingAccessibility) === null || _j === void 0 ? void 0 : _j.elevatorImages) === null || _k === void 0 ? void 0 : _k.map(img => img.imageUrl)) !== null && _l !== void 0 ? _l : [];
    const renderSection = () => {
        var _a, _b, _c, _d;
        switch (category) {
            case InaccurateInfoCategoryDto.PlaceEntrance:
                return (_jsx(SectionContainer, { children: _jsx(PlaceEntranceCorrectionSection, { stairInfo: placeCorrection.stairInfo, stairHeightLevel: placeCorrection.stairHeightLevel, hasSlope: placeCorrection.hasSlope, doorDirectionType: placeCorrection.doorDirectionType, isStandaloneBuilding: (_a = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _a === void 0 ? void 0 : _a.isStandaloneBuilding, existingEntrancePhotoUrls: entranceImageUrls, newEntrancePhotos: newEntrancePhotos, deletedEntrancePhotoIndices: deletedEntrancePhotoIndices, replacedEntrancePhotos: replacedEntrancePhotos, onChangeStairInfo: value => {
                            updatePlaceField('stairInfo', value);
                            if (value !== StairInfo.One) {
                                updatePlaceField('stairHeightLevel', undefined);
                            }
                        }, onChangeStairHeightLevel: value => updatePlaceField('stairHeightLevel', value), onChangeHasSlope: value => updatePlaceField('hasSlope', value), onChangeDoorDirectionType: value => updatePlaceField('doorDirectionType', value), onDeleteExistingEntrancePhoto: handleDeleteExistingEntrancePhoto, onReplaceExistingEntrancePhoto: handleReplaceExistingEntrancePhoto, onChangeNewEntrancePhotos: setNewEntrancePhotos }) }));
            case InaccurateInfoCategoryDto.BuildingEntrance:
                return (_jsx(SectionContainer, { children: _jsx(BuildingEntranceCorrectionSection, { entranceStairInfo: buildingCorrection.entranceStairInfo, entranceStairHeightLevel: buildingCorrection.entranceStairHeightLevel, hasSlope: buildingCorrection.hasSlope, entranceDoorTypes: buildingCorrection.entranceDoorTypes, existingBaEntrancePhotoUrls: baEntranceImageUrls, newBaEntrancePhotos: newBaEntrancePhotos, deletedBaEntrancePhotoIndices: deletedBaEntrancePhotoIndices, replacedBaEntrancePhotos: replacedBaEntrancePhotos, onChangeEntranceStairInfo: value => {
                            setBuildingCorrection(prev => (Object.assign(Object.assign({}, prev), { entranceStairInfo: value, entranceStairHeightLevel: value !== StairInfo.One
                                    ? undefined
                                    : prev.entranceStairHeightLevel })));
                        }, onChangeEntranceStairHeightLevel: value => setBuildingCorrection(prev => (Object.assign(Object.assign({}, prev), { entranceStairHeightLevel: value }))), onChangeHasSlope: value => setBuildingCorrection(prev => (Object.assign(Object.assign({}, prev), { hasSlope: value }))), onChangeEntranceDoorTypes: value => setBuildingCorrection(prev => (Object.assign(Object.assign({}, prev), { entranceDoorTypes: value }))), onDeleteExistingBaEntrancePhoto: handleDeleteExistingBaEntrancePhoto, onReplaceExistingBaEntrancePhoto: handleReplaceExistingBaEntrancePhoto, onChangeNewBaEntrancePhotos: setNewBaEntrancePhotos }) }));
            case InaccurateInfoCategoryDto.Floor:
                return (_jsx(SectionContainer, { children: _jsx(FloorCorrectionSection, { floors: placeCorrection.floors, floorMovingMethodTypes: placeCorrection.floorMovingMethodTypes, isStandaloneBuilding: (_b = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _b === void 0 ? void 0 : _b.isStandaloneBuilding, elevatorAccessibility: placeCorrection.elevatorAccessibility, onChangeFloors: value => updatePlaceField('floors', value), onChangeFloorMovingMethodTypes: value => updatePlaceField('floorMovingMethodTypes', value), onChangeElevatorAccessibility: value => updatePlaceField('elevatorAccessibility', value), onStateChange: setFloorFormState }) }));
            case InaccurateInfoCategoryDto.DoorType:
                return (_jsx(SectionContainer, { children: _jsx(DoorTypeCorrectionSection, { entranceDoorTypes: placeCorrection.entranceDoorTypes, onChangeDoorTypes: value => updatePlaceField('entranceDoorTypes', value) }) }));
            case InaccurateInfoCategoryDto.Elevator: {
                const isBaTarget = elevatorTarget === ElevatorCorrectionTargetDto.Ba;
                return (_jsx(SectionContainer, { children: _jsx(ElevatorCorrectionSection, { elevatorAccessibility: isBaTarget
                            ? buildingCorrection.elevatorAccessibility
                            : placeCorrection.elevatorAccessibility, existingElevatorPhotoUrls: isBaTarget ? baElevatorImageUrls : elevatorImageUrls, newElevatorPhotos: isBaTarget ? newBaElevatorPhotos : newElevatorPhotos, deletedElevatorPhotoIndices: isBaTarget
                            ? deletedBaElevatorPhotoIndices
                            : deletedElevatorPhotoIndices, replacedElevatorPhotos: isBaTarget ? replacedBaElevatorPhotos : replacedElevatorPhotos, needsBaPhotos: false, existingBaElevatorPhotoUrls: [], newBaElevatorPhotos: [], deletedBaElevatorPhotoIndices: [], replacedBaElevatorPhotos: new Map(), onChangeElevatorAccessibility: value => {
                            if (isBaTarget) {
                                setBuildingCorrection(prev => {
                                    if (value !== undefined) {
                                        return Object.assign(Object.assign({}, prev), { hasElevator: true, elevatorAccessibility: {
                                                stairInfo: value.stairInfo,
                                                stairHeightLevel: value.stairHeightLevel,
                                                hasSlope: value.hasSlope,
                                            } });
                                    }
                                    else {
                                        const { elevatorAccessibility: _ } = prev, rest = __rest(prev, ["elevatorAccessibility"]);
                                        return Object.assign(Object.assign({}, rest), { hasElevator: false });
                                    }
                                });
                            }
                            else {
                                setPlaceCorrection(prev => (Object.assign(Object.assign({}, prev), { elevatorAccessibility: value })));
                            }
                        }, onDeleteExistingElevatorPhoto: isBaTarget
                            ? handleDeleteExistingBaElevatorPhoto
                            : handleDeleteExistingElevatorPhoto, onReplaceExistingElevatorPhoto: isBaTarget
                            ? handleReplaceExistingBaElevatorPhoto
                            : handleReplaceExistingElevatorPhoto, onChangeNewElevatorPhotos: isBaTarget ? setNewBaElevatorPhotos : setNewElevatorPhotos, onDeleteExistingBaElevatorPhoto: handleDeleteExistingBaElevatorPhoto, onReplaceExistingBaElevatorPhoto: handleReplaceExistingBaElevatorPhoto, onChangeNewBaElevatorPhotos: setNewBaElevatorPhotos }) }));
            }
            case InaccurateInfoCategoryDto.AccessLevel:
                return (_jsx(SectionContainer, { children: _jsx(AccessLevelCorrectionSection, { currentLevel: stairFieldsToAccessLevel((_c = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _c === void 0 ? void 0 : _c.stairInfo, (_d = accessibilityData === null || accessibilityData === void 0 ? void 0 : accessibilityData.placeAccessibility) === null || _d === void 0 ? void 0 : _d.stairHeightLevel), selectedLevel: selectedAccessLevel, onChangeLevel: setSelectedAccessLevel }) }));
            case InaccurateInfoCategoryDto.Photo:
                return (_jsx(SectionContainer, { children: _jsx(PhotoCorrectionSection, { entranceImageUrls: entranceImageUrls, elevatorImageUrls: elevatorImageUrls, newEntrancePhotos: newEntrancePhotos, newElevatorPhotos: newElevatorPhotos, deletedEntrancePhotoIndices: deletedEntrancePhotoIndices, deletedElevatorPhotoIndices: deletedElevatorPhotoIndices, replacedEntrancePhotos: replacedEntrancePhotos, replacedElevatorPhotos: replacedElevatorPhotos, baEntranceImageUrls: baEntranceImageUrls, baElevatorImageUrls: baElevatorImageUrls, newBaEntrancePhotos: newBaEntrancePhotos, newBaElevatorPhotos: newBaElevatorPhotos, deletedBaEntrancePhotoIndices: deletedBaEntrancePhotoIndices, deletedBaElevatorPhotoIndices: deletedBaElevatorPhotoIndices, replacedBaEntrancePhotos: replacedBaEntrancePhotos, replacedBaElevatorPhotos: replacedBaElevatorPhotos, showPlaceElevatorPhotos: showPlaceElevatorPhotos, showBaEntrancePhotos: showBaEntrancePhotos, showBaElevatorPhotos: showBaElevatorPhotos, onDeleteExistingEntrancePhoto: handleDeleteExistingEntrancePhoto, onDeleteExistingElevatorPhoto: handleDeleteExistingElevatorPhoto, onReplaceExistingEntrancePhoto: handleReplaceExistingEntrancePhoto, onReplaceExistingElevatorPhoto: handleReplaceExistingElevatorPhoto, onChangeNewEntrancePhotos: setNewEntrancePhotos, onChangeNewElevatorPhotos: setNewElevatorPhotos, onDeleteExistingBaEntrancePhoto: handleDeleteExistingBaEntrancePhoto, onDeleteExistingBaElevatorPhoto: handleDeleteExistingBaElevatorPhoto, onReplaceExistingBaEntrancePhoto: handleReplaceExistingBaEntrancePhoto, onReplaceExistingBaElevatorPhoto: handleReplaceExistingBaElevatorPhoto, onChangeNewBaEntrancePhotos: setNewBaEntrancePhotos, onChangeNewBaElevatorPhotos: setNewBaElevatorPhotos }) }));
            default:
                return null;
        }
    };
    return (_jsxs(ScreenLayout, { isHeaderVisible: true, isKeyboardAvoidingView: true, children: [_jsxs(ScrollView, { contentContainerStyle: { paddingBottom: 40 }, children: [_jsxs(PageHeader, { children: [_jsx(PageTitle, { children: "\uC62C\uBC14\uB978 \uC815\uBCF4\uB85C \uC218\uC815\uD574\uC8FC\uC138\uC694." }), _jsx(PageDescription, { children: "\uC798\uBABB\uB418\uC5C8\uAC70\uB098 \uC218\uC815\uC774 \uD544\uC694\uD55C \uC815\uBCF4\uB97C \uC54C\uB824\uC8FC\uC138\uC694." })] }), _jsxs(FormBody, { children: [renderSection(), _jsxs(NoteSectionContainer, { children: [_jsx(NoteLabel, { children: "\uBD80\uC5F0 \uC124\uBA85" }), _jsx(TextAreaV2, { placeholder: "\uCD94\uAC00\uB85C \uC124\uBA85\uD560 \uB0B4\uC6A9\uC774 \uC788\uB2E4\uBA74 \uC54C\uB824\uC8FC\uC138\uC694", value: noteText, onChangeText: setNoteText })] }), _jsx(SubmitButtonContainer, { children: _jsx(SccPrimaryButton, { text: "\uC81C\uCD9C\uD558\uAE30", isDisabled: isSubmitDisabled, onPress: handleSubmitPress, elementName: "report_correction_submit" }) })] })] }), _jsx(UploadProgressOverlay, Object.assign({}, uploadProgress))] }));
}
const LoadingContainer = styled.View `
  flex: 1;
  justify-content: center;
  align-items: center;
`;
const PageHeader = styled.View `
  background-color: ${color.gray10};
  gap: 8px;
  padding: 24px 20px;
`;
const PageTitle = styled.Text `
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.4px;
  font-family: ${font.pretendardSemibold};
  color: ${color.gray90v2};
`;
const PageDescription = styled.Text `
  font-size: 15px;
  line-height: 22px;
  letter-spacing: -0.3px;
  font-family: ${font.pretendardRegular};
  color: ${color.gray60v2};
`;
const FormBody = styled.View `
  padding: 30px 20px;
  gap: 48px;
`;
const SectionContainer = styled.View ``;
const NoteSectionContainer = styled.View `
  gap: 16px;
`;
const NoteLabel = styled.Text `
  font-size: 18px;
  line-height: 26px;
  letter-spacing: -0.36px;
  font-family: ${font.pretendardSemibold};
  color: ${color.gray80v2};
`;
const SubmitButtonContainer = styled.View ``;
